import EnabledCheckIcon from '@/components/Icons/EnabledCheckIcon';
import UploadIcon from '@/components/Icons/UploadIcon';
import { validateFile } from '@/helpers/creativeLibrary';
import type { InlineCreativeType } from '@/types/creativeLibrary';
import { Cancel } from '@mui/icons-material';
import { Box } from '@mui/material';
import classNames from 'classnames';
import { memo, useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { toast } from 'react-toastify';
import FileValidationErrorModal from './FileValidationErrorModal';
import { fileUploadHints } from './constants';

interface IFileUploadProps {
  format: InlineCreativeType;
  loading: boolean;
  failed?: boolean;
  disabled?: boolean;
  setUploadedFile: (file: File) => void;
}

const baseDndClassname = 'flex justify-center items-center flex-col w-full h-[214px] border rounded-lg';

const FileUpload = memo(({ format, loading, failed, setUploadedFile, disabled }: IFileUploadProps) => {
  const [attachedFileName, setAttachedFileName] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [hasUploadFailed, setHasUploadFailed] = useState(false);

  const classNameArgs = {
    'cursor-pointer': !disabled,
  };

  useEffect(() => {
    if (failed) {
      setHasUploadFailed(true);
    }
  }, [failed]);

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      setErrorMessage('');
      setHasUploadFailed(false);
      try {
        const [file] = acceptedFiles;

        if (!file) {
          toast.info('Please select a file');
          return;
        }

        await validateFile(file, format, setErrorMessage);

        setAttachedFileName(file.name);
        setUploadedFile(file);
      } catch (err) {
        setHasUploadFailed(true);
        toast.error(`Error uploading file: ${(err as Error).message}`);
      }
    },
    [format, setUploadedFile],
  );

  const { getRootProps, getInputProps } = useDropzone({ onDrop });

  const uploadFailedContent = (
    <Box
      {...getRootProps()}
      className={classNames(baseDndClassname, 'bg-[#FF3B30] bg-opacity-[.2] border-[#ADACB0]', classNameArgs)}
    >
      <Cancel color='error' sx={{ width: 40, height: 40 }} />
      <span className='text-center text-lg font-semibold justify-between mt-2'>File Upload Failed</span>
      {attachedFileName ? (
        <span className='text-sm font-semibold mt-3'>Try submitting the form again</span>
      ) : (
        <>
          <span className='text-center text-sm font-semibold justify-between mt-3 cursor-pointer'>
            Choose file to retry upload
          </span>
          <input {...getInputProps()} />
        </>
      )}
    </Box>
  );

  const attachFileContent = (
    <Box
      {...getRootProps()}
      className={classNames(baseDndClassname, 'bg-[#F8F8F8] pr-3 border-dashed border-[#ADACB0]', classNameArgs)}
    >
      <input disabled={disabled} {...getInputProps()} />
      <div className='bg-[#E6E6E7] rounded-lg p-2.5'>
        <UploadIcon />
      </div>
      <span className='mt-2 text-sm font-medium'>Drag & drop files or browse files</span>
      <div className='flex mt-1.5 flex-col justify-center items-center'>
        {(fileUploadHints[format] ?? []).map((string: string) => (
          <span key={string} className='text-[#8E8F94] text-sm font-normal text-center'>
            {string}
          </span>
        ))}
      </div>
      <div className='flex w-full justify-center'></div>
    </Box>
  );

  const fileAttachedContent = (
    <Box className={classNames(baseDndClassname, 'bg-[#2C6E49] bg-opacity-[.15] pr-3 border-[#1b4332]', classNameArgs)}>
      <EnabledCheckIcon width={40} height={40} />
      <span className='text-center text-lg font-semibold justify-between mt-2'>File Attached</span>
      <span className='mt-2 text-[#1b4332] text-sm font-medium'>{attachedFileName}</span>
      {loading ? (
        <span className='text-sm font-semibold mt-3'>Uploading...</span>
      ) : (
        <>
          <span
            {...getRootProps()}
            className='text-center text-sm font-semibold justify-between mt-3 cursor-pointer hover:text-[#1b4332]'
          >
            Choose another file
          </span>
          <input {...getInputProps()} />
        </>
      )}
    </Box>
  );

  return (
    <>
      {hasUploadFailed ? uploadFailedContent : !attachedFileName ? attachFileContent : fileAttachedContent}
      <FileValidationErrorModal open={!!errorMessage} message={errorMessage} onClose={() => setErrorMessage('')} />
    </>
  );
});

export default FileUpload;
